import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import {
  AdminRelatedLinks,
  AdminBlogShell,
  AdminCurrentImagePreview,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { LoadingSpinner } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  createYowanAdminFacility,
  getYowanAdminMutationErrorMessage,
  validateYowanImageUploadFile,
  YOWAN_IMAGE_UPLOAD_HELP_TEXT,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const FieldNote = ({ children, style }) => {
  if (!children) {
    return null;
  }

  return (
    <p
      className="yowan-admin-form__field-note"
      style={style || undefined}
    >
      {children}
    </p>
  );
};

const AdminFacilityEditorFields = ({
  content,
  register,
  errors,
  roomIndexes = [0],
  onAddRoom,
  onRemoveRoom,
  currentImage,
}) => {
  const { labels, helpText, errors: formErrors } = content.form;
  const roomLabels = content.form.roomLabels || {};

  const roomSectionStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 20rem), 1fr))',
    gap: '0.85rem',
    marginTop: '1.5rem',
    padding: '1rem',
    borderRadius: '1rem',
    background: 'rgba(89, 102, 116, 0.08)',
  };
  const roomHeaderStyle = {
    display: 'flex',
    gridColumn: '1 / -1',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.65rem',
  };
  const roomTitleStyle = {
    margin: 0,
    color: '#24303a',
    fontSize: '1rem',
    fontWeight: 700,
  };
  const roomButtonStyle = {
    minHeight: '2rem',
    padding: '0.32rem 0.9rem',
    border: 'none',
    borderRadius: '999px',
    background: 'rgba(116, 126, 115, 0.16)',
    color: '#2f363d',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
  };
  const roomCardStyle = {
    display: 'grid',
    gap: '0.7rem',
    padding: '0.85rem',
    border: '1px solid rgba(89, 102, 116, 0.14)',
    borderRadius: '0.85rem',
    background: 'rgba(255, 255, 255, 0.68)',
  };
  const roomCardHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.75rem',
  };
  const roomRemoveButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '3.4rem',
    minHeight: '1.8rem',
    padding: '0.2rem 0.65rem',
    border: 'none',
    borderRadius: '999px',
    background: 'rgba(124, 42, 46, 0.12)',
    color: '#7c2a2e',
    cursor: 'pointer',
    fontSize: '0.82rem',
    fontWeight: 700,
    lineHeight: 1,
  };
  return (
    <>
      <div className="c-form__field">
        <span className="c-form__label">
          {labels.facilityName}
          <em className="c-form__required">*</em>
        </span>
        <input
          className="c-form__control"
          type="text"
          {...register('facilityName', { required: formErrors.required })}
        />
        {errors.facilityName ? (
          <em className="yowan-admin-form__error">{errors.facilityName.message}</em>
        ) : null}
        <FieldNote>{helpText.facilityName}</FieldNote>
      </div>

      <div className="c-form__field">
        <span className="c-form__label">
          {labels.shortName}
          <em className="c-form__required">*</em>
        </span>
        <input
          className="c-form__control"
          type="text"
          {...register('shortName', { required: formErrors.required })}
        />
        {errors.shortName ? (
          <em className="yowan-admin-form__error">{errors.shortName.message}</em>
        ) : null}
        <FieldNote>{helpText.shortName}</FieldNote>
      </div>

      <div className="c-form__field">
        <span className="c-form__label">
          {labels.address}
          <em className="c-form__required">*</em>
        </span>
        <textarea
          className="c-form__control"
          rows="3"
          {...register('address', { required: formErrors.required })}
        />
        {errors.address ? (
          <em className="yowan-admin-form__error">{errors.address.message}</em>
        ) : null}
        <FieldNote>{helpText.address}</FieldNote>
      </div>

      <div className="c-form__field">
        <span className="c-form__label">
          {labels.phone}
        </span>
        <input
          className="c-form__control"
          type="tel"
          {...register('phone')}
        />
        {errors.phone ? (
          <em className="yowan-admin-form__error">{errors.phone.message}</em>
        ) : null}
        <FieldNote>{helpText.phone}</FieldNote>
      </div>

      <div className="c-form__field">
        <span className="c-form__label">
          {labels.gmap}
          <em className="c-form__required">*</em>
        </span>
        <input
          className="c-form__control"
          type="url"
          {...register('gmap', { required: formErrors.required })}
        />
        {errors.gmap ? (
          <em className="yowan-admin-form__error">{errors.gmap.message}</em>
        ) : null}
        <FieldNote>{helpText.gmap}</FieldNote>
      </div>

      <div className="c-form__field">
        <span className="c-form__label">{labels.url}</span>
        <input className="c-form__control" type="url" {...register('url')} />
      </div>

      <div className="c-form__field">
        <span className="c-form__label">{labels.publicLocationLabel}</span>
        <input className="c-form__control" type="text" {...register('publicLocationLabel')} />
        <FieldNote>{helpText.publicLocationLabel}</FieldNote>
      </div>

      <div className="c-form__field">
        <span className="c-form__label">{labels.accessNote}</span>
        <textarea className="c-form__control" rows="3" {...register('accessNote')} />
        <FieldNote>{helpText.accessNote}</FieldNote>
      </div>

      <div className="c-form__field">
        <span className="c-form__label">{labels.privateLocationNote}</span>
        <textarea className="c-form__control" rows="3" {...register('privateLocationNote')} />
        <FieldNote>{helpText.privateLocationNote}</FieldNote>
      </div>

      <div className="c-form__field">
        <span className="c-form__label">{labels.image}</span>
        <input type="hidden" {...register('primary_image_id')} />
        <AdminCurrentImagePreview image={currentImage} />
        <input className="c-form__control" type="file" accept="image/*" {...register('image', { validate: validateYowanImageUploadFile })} />
        {errors.image ? (
          <em className="yowan-admin-form__error">{errors.image.message}</em>
        ) : null}
        <FieldNote>{[helpText.image, YOWAN_IMAGE_UPLOAD_HELP_TEXT].filter(Boolean).join(' ')}</FieldNote>
      </div>

      <div className="c-form__field yowan-admin-form__toggle-field">
        <span className="c-form__label">{labels.isActive}</span>
        <label className="c-form__choice yowan-admin-form__toggle-choice">
          <input type="checkbox" {...register('isActive')} />
          <span>{labels.isActive}</span>
        </label>
        <FieldNote>{helpText.isActive}</FieldNote>
      </div>

      <div className="c-form__field">
        <span className="c-form__label">{labels.remarks}</span>
        <textarea className="c-form__control" rows="4" {...register('remarks')} />
      </div>

      <section style={roomSectionStyle}>
        <div style={roomHeaderStyle}>
          <div>
            <h2 style={roomTitleStyle}>{roomLabels.sectionTitle}</h2>
            {roomLabels.sectionLead ? (
              <p className="yowan-admin-form__field-note" style={{ margin: '0.3rem 0 0' }}>
                {roomLabels.sectionLead}
              </p>
            ) : null}
          </div>
          <button type="button" style={roomButtonStyle} onClick={onAddRoom}>
            + {roomLabels.addButton}
          </button>
        </div>

        {roomIndexes.map((roomIndex, roomPosition) => (
          <div key={roomIndex} style={roomCardStyle}>
            <input type="hidden" {...register(`rooms.${roomIndex}.id`)} />
            <div style={roomCardHeaderStyle}>
              <h3 style={roomTitleStyle}>
                {roomLabels.itemTitle} {roomPosition + 1}
              </h3>
              {roomIndexes.length > 1 ? (
                <button
                  type="button"
                  style={roomRemoveButtonStyle}
                  aria-label={`${roomLabels.itemTitle} ${roomPosition + 1}を入力欄から外す`}
                  onClick={() => onRemoveRoom(roomIndex)}
                >
                  外す
                </button>
              ) : null}
            </div>

            <div className="c-form__field">
              <span className="c-form__label">
                {roomLabels.roomName}
                <em className="c-form__required">*</em>
              </span>
              <input
                className="c-form__control"
                type="text"
                {...register(`rooms.${roomIndex}.roomName`, { required: formErrors.required })}
              />
              {errors?.rooms?.[roomIndex]?.roomName ? (
                <em className="yowan-admin-form__error">{errors.rooms[roomIndex].roomName.message}</em>
              ) : null}
            </div>

            <div className="c-form__field">
              <span className="c-form__label">{roomLabels.capacity}</span>
              <input className="c-form__control" type="number" min="0" {...register(`rooms.${roomIndex}.capacity`)} />
            </div>

            <div className="c-form__field yowan-admin-form__toggle-field">
              <span className="c-form__label">{roomLabels.isActive}</span>
              <label className="c-form__choice yowan-admin-form__toggle-choice">
                <input type="checkbox" {...register(`rooms.${roomIndex}.isActive`)} />
                <span>{roomLabels.isActive}</span>
              </label>
            </div>

            <div className="c-form__field">
              <span className="c-form__label">{roomLabels.remarks}</span>
              <textarea className="c-form__control" rows="3" {...register(`rooms.${roomIndex}.remarks`)} />
            </div>
          </div>
        ))}
      </section>
    </>
  );
};

const AdminFacilitiesCreatePage = () => {
  const content = yowanPageContent['/admin/events/facilities/new'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const navigate = useNavigate();
  const [submitMessage, setSubmitMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [roomIndexes, setRoomIndexes] = useState(
    Array.from({ length: content?.form.defaults?.rooms?.length || 1 }, (_, index) => index),
  );
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: content?.form.defaults,
    shouldUnregister: true,
  });

  if (!content) {
    return null;
  }

  const onSubmit = async (values) => {
    setIsSubmitting(true);
    setSubmitMessage(content.form.pendingMessage);

    try {
      await createYowanAdminFacility(values);
      setSubmitMessage('保存しました。');
      navigate(appendYowanPreviewQueryIfNeeded('/admin/events/facilities', preservedDomainSearch));
    } catch (error) {
      setSubmitMessage(getYowanAdminMutationErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleAddRoom = () => {
    setRoomIndexes((currentIndexes) => [
      ...currentIndexes,
      currentIndexes.length ? Math.max(...currentIndexes) + 1 : 0,
    ]);
  };
  const handleRemoveRoom = (roomIndex) => {
    setRoomIndexes((currentIndexes) => {
      if (currentIndexes.length <= 1) {
        return currentIndexes;
      }

      return currentIndexes.filter((currentIndex) => currentIndex !== roomIndex);
    });
  };

  return (
    <AdminBlogShell activeNavHref="/admin/events/facilities" routeKey="/admin/events/facilities/new">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="back-to-list" to={appendYowanPreviewQueryIfNeeded('/admin/events/facilities', preservedDomainSearch)}>
            一覧へ戻る
          </Link>,
        ]}
      />
      <AdminRelatedLinks links={content.relatedLinks} preservedDomainSearch={preservedDomainSearch} />

      <form className="yowan-admin-form c-surface-content" onSubmit={handleSubmit(onSubmit)}>
        <AdminFacilityEditorFields
          content={content}
          register={register}
          errors={errors}
          roomIndexes={roomIndexes}
          onAddRoom={handleAddRoom}
          onRemoveRoom={handleRemoveRoom}
        />

        <div className="yowan-admin-form__actions">
          <button type="submit" className="c-form__submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <LoadingSpinner size="sm" />
                {content.form.pendingMessage}
              </>
            ) : content.form.submitLabel}
          </button>
        </div>

        {submitMessage ? <p className="yowan-admin-form__message">{submitMessage}</p> : null}
      </form>
    </AdminBlogShell>
  );
};

export { AdminFacilityEditorFields };
export default AdminFacilitiesCreatePage;
